import { useForm, Controller } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { caseSchema, CaseFormData } from '../../lib/schemas/caseSchema'
import { CASE_STATUS, CASE_ORIGIN, CASE_PRIORITY } from '../../constants'
import { mockContacts, mockAccounts } from '../../lib/api/mockData'
import { FormInput, FormSelect, FormDialog } from '../../components/ui'

interface CaseFormProps {
  open: boolean
  onClose: () => void
  onSubmit: (data: any) => void
  initialData?: any
}

export const CaseForm = ({ open, onClose, onSubmit, initialData }: CaseFormProps) => {
  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<CaseFormData>({
    resolver: zodResolver(caseSchema),
    defaultValues: initialData || {
      status:      'New',
      origin:      'Email',
      priority:    'Medium',
      subject:     '',
      contactName: '',
      accountName: '',
      description: '',
    },
  })

  const handleClose = () => {
    reset()
    onClose()
  }

  const handleFormSubmit = (data: CaseFormData) => {
    onSubmit({ ...data, owner: 'Sarah Johnson' })
    handleClose()
  }

  return (
    <FormDialog
      open={open}
      onClose={handleClose}
      onSave={handleSubmit(handleFormSubmit)}
      title={initialData ? 'Edit Case' : 'New Case'}
      subtitle="* = Required information"
      saving={isSubmitting}
      maxWidth="sm"
    >
      <Controller
        name="subject"
        control={control}
        render={({ field }) => (
          <FormInput
            {...field}
            label="Subject"
            required
            placeholder="Brief description of the issue"
            error={!!errors.subject}
            helperText={errors.subject?.message}
          />
        )}
      />

      <Controller
        name="status"
        control={control}
        render={({ field }) => (
          <FormSelect
            label="Status"
            required
            options={CASE_STATUS}
            value={field.value ?? null}
            onChange={(v) => field.onChange(v ?? 'New')}
            error={!!errors.status}
            helperText={errors.status?.message}
          />
        )}
      />

      <Controller
        name="origin"
        control={control}
        render={({ field }) => (
          <FormSelect
            label="Origin"
            options={CASE_ORIGIN}
            value={field.value ?? null}
            onChange={(v) => field.onChange(v ?? 'Email')}
            error={!!errors.origin}
            helperText={errors.origin?.message}
          />
        )}
      />

      <Controller
        name="priority"
        control={control}
        render={({ field }) => (
          <FormSelect
            label="Priority"
            options={CASE_PRIORITY}
            value={field.value ?? null}
            onChange={(v) => field.onChange(v ?? 'Medium')}
            error={!!errors.priority}
            helperText={errors.priority?.message}
          />
        )}
      />

      <Controller
        name="contactName"
        control={control}
        render={({ field }) => (
          <FormSelect
            label="Contact Name"
            options={mockContacts.map((c) => `${c.firstName} ${c.lastName}`)}
            value={field.value ?? null}
            onChange={(v) => field.onChange(v ?? '')}
            error={!!errors.contactName}
            helperText={errors.contactName?.message}
          />
        )}
      />

      <Controller
        name="accountName"
        control={control}
        render={({ field }) => (
          <FormSelect
            label="Account Name"
            options={mockAccounts.map((a) => a.name)}
            value={field.value ?? null}
            onChange={(v) => field.onChange(v ?? '')}
            error={!!errors.accountName}
            helperText={errors.accountName?.message}
          />
        )}
      />

      <Controller
        name="description"
        control={control}
        render={({ field }) => (
          <FormInput
            {...field}
            label="Description"
            multiline
            rows={3}
            placeholder="Describe the issue in detail…"
            error={!!errors.description}
            helperText={errors.description?.message}
          />
        )}
      />
    </FormDialog>
  )
}
